import { zApiError } from '@lk/shared';
import { ApiHttpError, ApiParseError, NetworkError } from './errors';

/**
 * Структурний тип «щось, що вміє розібрати відповідь».
 *
 * Навмисно НЕ імпортуємо тут `zod`: схеми приїжджають готовими з @lk/shared,
 * а вебзастосунок лишається незалежним від версії бібліотеки валідації.
 * Будь-яка zod-схема підходить під цей інтерфейс структурно.
 */
export interface ResponseSchema<T> {
  parse(value: unknown): T;
}

export const API_BASE = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

/**
 * Підписники на 401. Ми не тягнемо сюди роутер і стор, щоб http-шар
 * лишався чистим і тестованим без React. Хто хоче реагувати —
 * підписується (див. src/features/auth/authStore.ts).
 */
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function emitUnauthorized(): void {
  for (const listener of unauthorizedListeners) {
    try {
      listener();
    } catch {
      /* один поганий підписник не має ламати решту */
    }
  }
}

/**
 * Кеші Cache Storage, які однакові для будь-якого користувача пристрою:
 * бренд-картинки, шрифти, звуки й прекеш оболонки від Workbox
 * (його імена завжди містять `precache`).
 *
 * Усе, чого немає в цьому списку, вважаємо слідом конкретної сесії й зносимо
 * при виході. Перелічуємо саме те, що лишається, а не те, що видаляти:
 * якщо колись зʼявиться новий кеш із персональними даними, він піде під ніж
 * автоматично, а не чекатиме, поки хтось згадає дописати правило.
 */
const SHARED_ASSET_CACHES = ['lk-brand', 'lk-fonts', 'lk-sounds'];

function isSharedAssetCache(name: string): boolean {
  return SHARED_ASSET_CACHES.includes(name) || name.includes('precache');
}

/**
 * Викорінює з Cache Storage усе, що могло належати користувачеві, який
 * виходить: інакше наступний, хто зайде з цього пристрою, ризикує побачити
 * чужі дані просто тому, що відповідь знайшлась у кеші.
 *
 * Статику лишаємо навмисно — вона нічия, а без неї наступна дитина
 * залишиться без офлайну до першого візиту з мережею.
 *
 * Повертає імена знесених кешів (зручно для тестів і діагностики).
 */
export async function purgeUserResponseCaches(): Promise<string[]> {
  // Cache Storage немає ні в jsdom, ні в браузері без HTTPS — це не помилка.
  if (typeof caches === 'undefined') return [];

  let names: string[];
  try {
    names = await caches.keys();
  } catch {
    return [];
  }

  const doomed = names.filter((name) => !isSharedAssetCache(name));
  await Promise.all(
    // Не даємо одному впертому кешу зірвати вихід: краще знести решту.
    doomed.map((name) => caches.delete(name).catch(() => false)),
  );
  return doomed;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Схема з контракту. Якщо задана — відповідь валідується, а не приймається на віру. */
  schema?: ResponseSchema<T>;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
  /** Не піднімати подію 401 (потрібно самому логіну). */
  silentUnauthorized?: boolean;
}

function buildUrl(path: string, query?: RequestOptions<unknown>['query']): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const { method = 'GET', body, schema, signal, query, silentUnauthorized } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: 'include',
      headers: body === undefined ? { Accept: 'application/json' } : {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // fetch кидає лише на мережевому рівні — це саме той випадок,
    // коли дані НЕ втрачені й запит треба повторити пізніше.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError('Немає звʼязку з сервером', cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const raw: unknown = await response
    .clone()
    .json()
    .catch(() => undefined);

  if (!response.ok) {
    if (response.status === 401 && !silentUnauthorized) emitUnauthorized();
    const parsed = zApiError.safeParse(raw);
    const code = parsed.success ? parsed.data.error.code : `http_${response.status}`;
    const message = parsed.success ? parsed.data.error.message : response.statusText || 'Помилка';
    const details = parsed.success ? parsed.data.error.details : raw;
    throw new ApiHttpError(response.status, code, message, details);
  }

  if (!schema) return raw as T;

  try {
    return schema.parse(raw);
  } catch (issues) {
    // Невідповідність контракту — це баг, і ми його показуємо, а не ковтаємо.
    throw new ApiParseError(path, issues);
  }
}
